import { AnumatiCardImage, buildCardQRCode, CARD_WIDTH } from 'components/anumati/AnumatiCardImage';
import domtoimage from 'dom-to-image';
import AnandDate from 'helpers/AnandDate';
import React from 'react';
import ReactDOM from 'react-dom';

/**
 * Renders an anumati record as a card image and hands it to the device share sheet.
 *
 * The card is mounted off screen, captured, then removed. Where the browser cannot share
 * files, which is mostly desktop, the image is copied to the clipboard instead so it can
 * be pasted straight into a chat.
 *
 * The capture is a PNG because that is the only image type browsers reliably accept on
 * the clipboard, and it is equally fine for sharing.
 */

const SHARED_STORAGE_PREFIX = 'anumati.shared.';

export type ShareCardResult = 'shared' | 'copied' | 'cancelled' | 'unsupported';

export function isCardShared(recordId: string): boolean {
	try {
		return !!recordId && window.localStorage.getItem(SHARED_STORAGE_PREFIX + recordId) === '1';
	} catch (e) {
		return false;
	}
}

export function markCardShared(recordId: string) {
	try {
		if (recordId) {
			window.localStorage.setItem(SHARED_STORAGE_PREFIX + recordId, '1');
		}
	} catch (e) {
		// Remembering the shared state is best effort.
	}
}

function waitForImages(container: HTMLElement) {
	let images = Array.from(container.querySelectorAll('img'));

	return Promise.all(
		images.map(
			(image) =>
				new Promise<void>((resolve) => {
					if (image.complete) {
						resolve();
						return;
					}

					image.onload = () => resolve();
					image.onerror = () => resolve();
				})
		)
	);
}

async function renderCardImage(record: any): Promise<Blob> {
	let qrCode = await buildCardQRCode(record['card_no'], record['people_count']);

	// Kept in the layout but invisible, because browsers skip rendering for elements that
	// are display:none, and the capture needs real geometry.
	let container = document.createElement('div');
	container.style.position = 'fixed';
	container.style.top = '0';
	container.style.left = '0';
	container.style.zIndex = '-1';
	container.style.opacity = '0';
	container.style.pointerEvents = 'none';
	container.style.width = CARD_WIDTH + 'px';
	document.body.appendChild(container);

	try {
		await new Promise<void>((resolve) => {
			ReactDOM.render(
				<AnumatiCardImage
					cardNo={record['card_no']}
					name={record['name']}
					reference={record['reference']}
					peopleCount={record['people_count']}
					arrival={new AnandDate(record['permission_date']).format('dd, Do MMM, YYYY')}
					qrCode={qrCode}
				/>,
				container,
				() => resolve()
			);
		});

		await waitForImages(container);

		return await domtoimage.toBlob(container.firstElementChild as HTMLElement);
	} finally {
		ReactDOM.unmountComponentAtNode(container);
		container.remove();
	}
}

async function copyImageToClipboard(blob: Blob): Promise<boolean> {
	let clipboardItem = (window as any).ClipboardItem;

	if (!navigator.clipboard || typeof clipboardItem !== 'function' || typeof (navigator.clipboard as any).write !== 'function') {
		return false;
	}

	try {
		await (navigator.clipboard as any).write([new clipboardItem({ [blob.type || 'image/png']: blob })]);
		return true;
	} catch (e) {
		return false;
	}
}

export async function shareAnumatiCard(record: any): Promise<ShareCardResult> {
	let blob = await renderCardImage(record);
	let file = new File([blob], `Shri Aagya-Patra ${record['card_no'] ?? ''}`.trim() + '.png', { type: blob.type || 'image/png' });

	// Only the image is shared. Adding a title or text makes the share sheet send a
	// separate text message alongside the card on apps such as WhatsApp.
	let shareData: any = { files: [file] };

	if (navigator.canShare && navigator.canShare(shareData) && navigator.share) {
		try {
			await navigator.share(shareData);
			return 'shared';
		} catch (e) {
			// A dismissed share sheet reports AbortError and must not look like a failure.
			if ((e as any)?.name === 'AbortError') {
				return 'cancelled';
			}

			// Anything else is worth falling back on rather than losing the card.
		}
	}

	return (await copyImageToClipboard(blob)) ? 'copied' : 'unsupported';
}
